// Centralized Authentication Service for ShopGlobal
// This service unifies all authentication logic and removes duplication

import { authApi, ApiError, setAuthToken, clearAuthToken } from "../api";

// Types
export interface User {
  id: string;
  email: string;
  fullName: string;
  delawareAddress: string;
  membershipTier: "free" | "premium" | "business";
  canadianAddress?: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
  verified: boolean;
  role: "user" | "admin";
  createdAt: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  error?: string;
  testOtp?: string;
  isNewUser?: boolean;
  message?: string;
}

// Auth Service Class
export class AuthService {
  private static instance: AuthService;
  private authState: AuthState = {
    user: null,
    token: null,
    loading: false,
    error: null,
  };

  private constructor() {
    this.initializeAuthState();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Initialize auth state from localStorage
  private initializeAuthState(): void {
    try {
      const token = getAuthToken();
      const userJson = localStorage.getItem("shopglobal_user");

      if (token && userJson) {
        const user = JSON.parse(userJson);
        this.authState = {
          user,
          token,
          loading: false,
          error: null,
        };
      } else {
        this.authState = {
          user: null,
          token: null,
          loading: false,
          error: null,
        };
      }
    } catch (error) {
      console.error("Failed to initialize auth state:", error);
      this.clearAuthState();
    }
  }

  // Get current auth state
  getAuthState(): AuthState {
    return { ...this.authState };
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!(this.authState.user && this.authState.token);
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.authState.user;
  }

  // Get current token
  getToken(): string | null {
    return this.authState.token;
  }

  // Start OTP flow (unified for login/signup)
  async startOtpFlow(email: string): Promise<AuthResponse> {
    try {
      this.setLoading(true);
      this.clearError();

      const response = await authApi.startSignup(email);

      return {
        success: true,
        testOtp: response.testOtp,
        message: response.message || "Verification code sent",
        isNewUser: response.isNewUser,
      };
    } catch (error) {
      const errorMessage = this.handleAuthError(error);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      this.setLoading(false);
    }
  }

  // Verify OTP and complete authentication
  async verifyOtp(email: string, otp: string): Promise<AuthResponse> {
    try {
      this.setLoading(true);
      this.clearError();

      const response = await authApi.completeSignup(email, otp, "", "");

      // The API returns the user and session directly, not wrapped in a success object
      if (response && response.user && response.session?.token) {
        const apiUser = response.user as any; // Type assertion for API response
        const user: User = {
          id: apiUser.id,
          email: apiUser.email,
          fullName: apiUser.fullName || apiUser.full_name || "",
          membershipTier:
            (apiUser.membershipTier?.toLowerCase() as
              | "free"
              | "premium"
              | "business") || "free",
          delawareAddress: apiUser.delawareAddress || "",
          canadianAddress: apiUser.canadianAddress
            ? typeof apiUser.canadianAddress === "string"
              ? JSON.parse(apiUser.canadianAddress)
              : apiUser.canadianAddress
            : {
                street: "",
                city: "",
                province: "",
                postalCode: "",
              },
          verified: true,
          role: "user" as const,
          createdAt: new Date().toISOString(),
        };

        // Update auth state
        this.authState = {
          user,
          token: response.session.token,
          loading: false,
          error: null,
        };

        // Persist to localStorage
        setAuthToken(response.session.token);
        localStorage.setItem("shopglobal_user", JSON.stringify(user));

        return {
          success: true,
          user,
        };
      } else {
        return {
          success: false,
          error: "Authentication failed",
        };
      }
    } catch (error) {
      const errorMessage = this.handleAuthError(error);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      this.setLoading(false);
    }
  }

  // Verify session with server
  async verifySession(): Promise<AuthResponse> {
    try {
      this.setLoading(true);
      this.clearError();

      const session = await authApi.verifySession();
      const user = this.getCurrentUser();

      if (session && user) {
        return {
          success: true,
          user,
        };
      } else {
        return {
          success: false,
          error: "Invalid session",
        };
      }
    } catch (error) {
      const errorMessage = this.handleAuthError(error);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      this.setLoading(false);
    }
  }

  // Logout
  logout(): void {
    this.clearAuthState();
    authApi.logout();
  }

  // Clear auth state
  private clearAuthState(): void {
    this.authState = {
      user: null,
      token: null,
      loading: false,
      error: null,
    };
    clearAuthToken();
  }

  // Set loading state
  private setLoading(loading: boolean): void {
    this.authState.loading = loading;
  }

  // Set error state
  private setError(error: string): void {
    this.authState.error = error;
  }

  // Clear error state
  private clearError(): void {
    this.authState.error = null;
  }

  // Handle auth errors consistently
  private handleAuthError(error: any): string {
    if (ApiError.isApiError(error)) {
      switch (error.statusCode) {
        case 401:
          return "Invalid credentials";
        case 403:
          return "Access denied";
        case 429:
          return "Too many attempts. Please try again later.";
        default:
          return error.message || "Authentication failed";
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return "An unexpected error occurred";
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();

// Convenience functions for backward compatibility
export const getCurrentUser = () => authService.getCurrentUser();
export const isAuthenticated = () => authService.isAuthenticated();
export const getAuthToken = () => authService.getToken();

// Re-export API functions for convenience
export { authApi } from "../api";
